import "@hotwired/turbo"

Turbo.session.drive = false
Turbo.session.history.stop() // Disable history cache
Turbo.config.forms.mode = "optin"

// CUSTOM TURBO STREAM ACTIONS

Turbo.StreamActions.full_page_redirect = function () {
  document.location = this.getAttribute("target")
}

Turbo.StreamActions.reload_page = function () {
  document.location.reload()
}

Turbo.StreamActions.show_popup_modal = function () {
  let url = this.getAttribute("target")
  $(function () {
    Turbo.visit(url, { frame: "popup_modal" })
  })
}

Turbo.StreamActions.track_pageview = function () {
  Activity.pageview(JSON.parse(this.getAttribute("properties")))
}

// The fragment and `view` stay: the response cannot tell whether the mobile
// filters overlay or the desktop map is open, so dropping `#filters` or
// `view=map` here would lose it from the address bar mid-change.
Turbo.StreamActions.history_replace = function () {
  const url = new URL(this.getAttribute("url"), document.baseURI)
  url.hash = location.hash
  const view = new URLSearchParams(location.search).get("view")
  if (view) url.searchParams.set("view", view)

  history.replaceState(history.state, "", url.href)
  document.dispatchEvent(new CustomEvent("turbo:history-replace"))
}

Turbo.StreamActions.capture_activity = function () {
  Activity.capture(
    this.getAttribute("event"),
    JSON.parse(this.getAttribute("properties")),
  )
}

// A proof the gate keeps refusing — a browser that rejects the cookie, an IP
// that moved between the solve and the retry — re-enters these actions on every
// solve, so without a budget they spin the gated endpoint and /challenge. One
// retry per target is what `search_map_controller` and
// `search_map_list_controller` spend before they give up too.
const spentProofOfWorkRetries = new Set()

document.addEventListener("turbo:load", () => spentProofOfWorkRetries.clear())

function claimProofOfWorkRetry(target) {
  if (spentProofOfWorkRetries.has(target)) return false

  spentProofOfWorkRetries.add(target)
  return true
}

Turbo.StreamActions.reload_on_proof_of_work = function () {
  const frameId = this.getAttribute("frame")
  const frame = document.getElementById(frameId)
  const widgets = frame.querySelectorAll("altcha-widget")
  const altcha = widgets[widgets.length - 1]

  if (!claimProofOfWorkRetry(frameId)) {
    altcha.remove()
    console.error("Proof of work did not unlock", frameId)
    return
  }

  altcha.addEventListener("statechange", (ev) => {
    if (ev.detail.state !== "verified") return

    altcha.remove()
    frame.reload()
  })
}

Turbo.StreamActions.retry_on_proof_of_work = function () {
  const url = this.getAttribute("url")
  const widgets = document.querySelectorAll("altcha-widget")
  const altcha = widgets[widgets.length - 1]

  if (!claimProofOfWorkRetry(url)) {
    altcha.remove()
    console.error("Proof of work did not unlock", url)
    return
  }

  altcha.addEventListener("statechange", (ev) => {
    if (ev.detail.state !== "verified") return

    altcha.remove()
    RailsRequest.get(url, { responseKind: "turbo-stream" })
  })
}

// Finds the target element by id, removes its first child element and
// places it just before the turbo stream tag. Since Turbo removes the
// stream tag immediately after executing it, this has the effect of
// replacing the stream tag with the first child of target, if it exists.
//
// It's usually going to be used with a hidden element that has some
// rendered items stored for later usage: e.g. storing unavailable offer
// search results so they can be poped later and placed in the visible
// results.
Turbo.StreamActions.shift_child_element = function () {
  let container = document.getElementById(this.getAttribute("target"))
  if (container.firstChild) {
    const firstChild = container.firstChild
    container.removeChild(firstChild)
    this.parentNode.insertBefore(firstChild, this)
  }
}

Turbo.StreamActions.emit_native_app_implicit_state = function () {
  window.ReactNativeWebView?.postMessage(
    JSON.stringify({
      action: "implicit_state",
      locale: this.getAttribute("locale"),
      currency: this.getAttribute("currency"),
      logged_in: this.getAttribute("logged_in"),
      email: this.getAttribute("email"),
    }),
  )
}

Turbo.StreamActions.post_message_to_native_app = function () {
  window.ReactNativeWebView?.postMessage(
    JSON.stringify({
      action: this.getAttribute("native_action"),
    }),
  )
}
// GLOBAL LISTENERS

function formSubmitButton(event) {
  return event.detail.formSubmission.submitter
}

// Needed for the fallback for old mobile app versions. They use
// onShouldStartLoadWithRequest callback of React Native Webview
// component. That callback doesn't fire if navigating directly
// from javascripy with `window.location.href = url` because that
// doesn't trigger the usual events. Creating a link element and
// clicking it triggers all the needed events.
function simulateLinkClick(url) {
  const link = document.createElement("a")
  link.href = url
  link.style.display = "none"
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

/**
 * This listener enables the integration with the native mobile app.
 * In specific cases, the native mobile app has implemented superior
 * UI for some screens. The integration works by the native app defining
 * a global `nativeMobileAppAbilities` object which we can query to see
 * what the current mobile app version supports.
 *
 * The abilities object can have the following keys:
 * - `supportedScreens`: list of screens for which the native app offers
 *   superior UI. By convention we are keeping the screen names the same
 *   as Turbo frame ids. This makes the logic simple. When Turbo is about
 *   to load a frame, if the id matches a supported screens it stops and
 *   hands it over to the native app to load it instead.
 */
addEventListener("turbo:before-fetch-request", (event) => {
  // Property Map should not be intercepted by the native app.
  // Not a good long-term solution. See https://github.com/halalbooking/halalbooking/pull/24447#issuecomment-2917400101
  const isPropertyMapPopup = /\/p\/\d+\/map$/.test(event.detail.url.pathname)
  const isCraftgateTaksitTable = event.detail.url.pathname.endsWith(
    "/craftgate/taksit_table",
  )
  // Partnership cashback modal lives in the booking situational JS pack (loaded on
  // /checkout). If the native app pushes it onto its own screen, the
  // checkout modal Stimulus controller is missing and Submit silently
  // no-ops. Keep it inside the popup_modal frame on /checkout instead.
  const isCashbackRequestNew = event.detail.url.pathname.endsWith(
    "/cashback_request/new",
  )
  if (
    document.body.classList.contains("native-webview") &&
    event.detail.fetchOptions.method.toUpperCase() === "GET" &&
    !isPropertyMapPopup &&
    !isCraftgateTaksitTable &&
    !isCashbackRequestNew
  ) {
    let target_frame = event.detail.fetchOptions.headers["Turbo-Frame"]
    if (window.nativeMobileAppAbilities) {
      let supportedScreens = window.nativeMobileAppAbilities.supportedScreens
      if (supportedScreens?.includes(target_frame)) {
        console.log(
          "Intercepting Turbo Loading the '" +
            target_frame +
            "' frame and loading in native app, url: " +
            event.detail.url,
        )
        event.preventDefault()
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            turbo: true,
            action: "open_screen",
            screen: target_frame,
            url: event.detail.url,
          }),
        )
      }
    } else if (target_frame == "popup_modal") {
      // This is temporary until all users are on a mobile app version that advertises `nativeMobileAppAbilities`.
      event.preventDefault()
      console.log(
        "Intercepting Turbo loading and doing a fallback old app redirect to: " +
          event.detail.url,
      )
      simulateLinkClick(event.detail.url)
    }
  }
})

addEventListener("turbo:submit-start", (event) => {
  formSubmitButton(event)?.classList.add("btn_loading")
  // Disabling the button is handled automatically by Turbo
})

addEventListener("turbo:submit-end", (event) => {
  formSubmitButton(event)?.classList.remove("btn_loading")
  // Enabling the button is handled automatically by Turbo
})

// Same flag as `#leavingPage` in checkout--payment_controller.js.
window.HB = window.HB || {}
window.HB.leavingPage = false

addEventListener("pagehide", () => {
  window.HB.leavingPage = true
})

addEventListener("pageshow", () => {
  window.HB.leavingPage = false
})

addEventListener("turbo:fetch-request-error", (event) => {
  const { request, error } = event.detail
  Activity.capture("turbo fetch-request error", {
    request_path: request.url.pathname,
    request_method: request.method,
    frame: event.target.closest?.("turbo-frame")?.id ?? null,
    leaving_page: window.HB.leavingPage,
    visibility_state: document.visibilityState,
    online: navigator.onLine,
    error_name: error.name,
    error_message: error.message,
  })
})

addEventListener("turbo:frame-missing", (event) => {
  let status = event.detail.response.status
  if (status >= 500 && status <= 599) {
    // 5xx means it was already logged as a server side error.
    return
  }

  let payload = {
    target: event.target.outerHTML,
    originUrl: document.location.toString(),
    requestUrl: event.detail.response.url,
    responseStatus: status,
    responseContentType: event.detail.response.headers.get("content-type"),
  }
  event.detail.response.text().then((responseBody) => {
    payload.responseBody = responseBody
    Bugsnag.notify("Turbo frame missing", {
      clientParams: payload,
      failedRequestId: event.detail.response.headers.get("x-request-id"),
    })
  })
})

// CONVENIENCE METHODS

Turbo.openLoginPopup = function (email) {
  this.visit("/sign-in?email=" + email, { frame: "popup_modal" })
}
